import React, { Suspense, lazy, useState, useContext, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Drawer,
  Hidden,
  IconButton,
  Button,
  Toolbar,
  Typography,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import AuthContext from "../hooks/authContext";
import CommonContext from "../hooks/commonContext";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Loader from "../components/AppLoader/Loader";
import { useMediaQuery } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { useTour } from "../components/ui/TourContext";
import { Moon, Sun } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { CommonConfirmationDialog } from "../components/DialogComponent/SupplyDataDialog";
import { setAnyUserData } from "../services/apiservices/employee";
import ReEngageModal from "../components/ReEngageModal";
import SessionWarningModal from "../components/SessionWarningModal";
import { updateSettings } from "../services/apiservices/settings";
import OnboardingModal from "../components/onboarding/OnboardingModal";
import NavbarProgressIndicator from "../components/onboarding/NavbarProgressIndicator";
import { useOnboardingExperience } from "../components/onboarding/OnboardingExperienceProvider";
const SidebarMenu = lazy(() => import("../components/SidebarMenu/SidebarMenu"));
const Footer = lazy(() => import("../components/footer/footer"));
const Header = lazy(() => import("../components/header/header"));
// const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      // width: drawerWidth,
      flexShrink: 0,
    },
  },
  logoutLink: {
    position: "fixed",
    bottom: 0,
    // width: 220,
    left: 0,
    // paddingLeft: 10,
    cursor: "pointer",
    // backgroundColor:'#343a45',
    "&:hover": {
      backgroundColor: "#00000044",
      cursor: "pointer",
    },
    "@media (max-width: 767px)": {
      width: 239,
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: "none",
    borderBottomColor: "#334155",
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    backgroundColor: "#2c3440"
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  toolbar: {
    ...theme.mixins.toolbar,
    // backgroundColor: "#2C3440",
    minHeight: "auto",
  },
  toolbarmirror: { ...theme.mixins.toolbar, backgroundColor: "transparent" },
  drawerPaper: {
    // width: drawerWidth,
    scrollbarWidth: "none",
    border: "none",
    boxShadow: "1px 2px 3px rgba(0,0,0,0.4)",
    backgroundColor: "#2C3440",
    overflowX: "hidden",
    "@media (max-width: 767px)": {
      width: 240,
    },
  },
  drawerPaper2: {
    // width: drawerWidth,
    scrollbarWidth: "none",
    border: "none",
    boxShadow: "1px 2px 3px rgba(0,0,0,0.4)",
    overflowY: "auto",
    overflowX: "hidden",
    "@media (max-width: 767px)": {
      width: 240,
    },
  },
  drawerPaperDark: {
    scrollbarWidth: "none",
    border: "none",
    boxShadow: "1px 2px 3px rgba(0,0,0,0.4)",
    backgroundColor: "#1A1F2C",
    overflowX: "hidden",
    "@media (max-width: 767px)": {
      width: 240,
    },
  },
  content: {
    flexGrow: 1,
  },
  btnLogout: {
    color: "#CBD5E1",
    fontSize: "14px",
    fontWeight: "700",
    "@media (max-width: 767px)": {
      fontSize: "19px",
    },
  },
}));

const LOADER_DELAY_MS = 280;
const ONBOARDING_WIDGET_CORNER_KEY = "onboarding_widget_corner";
const BILLING_POPUP_CLOSED_KEY = "billingPopupClosed";
const BILLING_POPUP_VISIBLE_DAYS = 5;
const SUBSCRIPTION_EXPIRY_CORNER_KEY = "subscription_expiry_corner";
const SUBSCRIPTION_EXPIRY_VISIBLE_DAYS = 15;
const ONBOARDING_WIDGET_CORNERS = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];

const getOnboardingWidgetOffsets = (isMobile) => ({
  "top-left": {
    top: isMobile ? 72 : 88,
    left: isMobile ? 12 : 20,
  },
  "top-right": {
    top: isMobile ? 72 : 88,
    right: isMobile ? 12 : 20,
  },
  "bottom-left": {
    bottom: isMobile ? 14 : 22,
    left: isMobile ? 12 : 20,
  },
  "bottom-right": {
    bottom: isMobile ? 14 : 22,
    right: isMobile ? 12 : 20,
  },
});

const getDefaultOnboardingWidgetCorner = () => "bottom-right";
const getDefaultSubscriptionExpiryCorner = () => "top-right";

const readSavedOnboardingWidgetCorner = () => {
  try {
    const savedCorner = localStorage.getItem(ONBOARDING_WIDGET_CORNER_KEY);
    return ONBOARDING_WIDGET_CORNERS.includes(savedCorner)
      ? savedCorner
      : getDefaultOnboardingWidgetCorner();
  } catch (_) {
    return getDefaultOnboardingWidgetCorner();
  }
};

const saveOnboardingWidgetCorner = (corner) => {
  try {
    localStorage.setItem(ONBOARDING_WIDGET_CORNER_KEY, corner);
  } catch (_) {}
};

const readSavedSubscriptionExpiryCorner = () => {
  try {
    const savedCorner = localStorage.getItem(SUBSCRIPTION_EXPIRY_CORNER_KEY);
    return ONBOARDING_WIDGET_CORNERS.includes(savedCorner)
      ? savedCorner
      : getDefaultSubscriptionExpiryCorner();
  } catch (_) {
    return getDefaultSubscriptionExpiryCorner();
  }
};

const saveSubscriptionExpiryCorner = (corner) => {
  try {
    localStorage.setItem(SUBSCRIPTION_EXPIRY_CORNER_KEY, corner);
  } catch (_) {}
};

const getNearestOnboardingWidgetCorner = (rect, isMobile) => {
  if (typeof window === "undefined") {
    return getDefaultOnboardingWidgetCorner();
  }

  const offsets = getOnboardingWidgetOffsets(isMobile);
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = rect?.width || (isMobile ? 240 : 260);
  const height = rect?.height || 92;
  const currentPoint = {
    x: rect?.left ?? viewportWidth - width - (offsets["bottom-right"].right || 0),
    y: rect?.top ?? viewportHeight - height - (offsets["bottom-right"].bottom || 0),
  };

  const cornerPoints = {
    "top-left": {
      x: offsets["top-left"].left || 0,
      y: offsets["top-left"].top || 0,
    },
    "top-right": {
      x: viewportWidth - width - (offsets["top-right"].right || 0),
      y: offsets["top-right"].top || 0,
    },
    "bottom-left": {
      x: offsets["bottom-left"].left || 0,
      y: viewportHeight - height - (offsets["bottom-left"].bottom || 0),
    },
    "bottom-right": {
      x: viewportWidth - width - (offsets["bottom-right"].right || 0),
      y: viewportHeight - height - (offsets["bottom-right"].bottom || 0),
    },
  };

  return Object.entries(cornerPoints).reduce(
    (closestCorner, [corner, point]) => {
      const distance = Math.hypot(point.x - currentPoint.x, point.y - currentPoint.y);
      if (distance < closestCorner.distance) {
        return { corner, distance };
      }
      return closestCorner;
    },
    { corner: getDefaultOnboardingWidgetCorner(), distance: Number.POSITIVE_INFINITY }
  ).corner;
};

const getSubscriptionExpiryOffsets = (isMobile, topNoticeHeight) => ({
  "top-left": {
    top: topNoticeHeight + (isMobile ? 92 : 76),
    left: isMobile ? 12 : 20,
  },
  "top-right": {
    top: topNoticeHeight + (isMobile ? 92 : 76),
    right: isMobile ? 12 : 20,
  },
  "bottom-left": {
    bottom: isMobile ? 14 : 22,
    left: isMobile ? 12 : 20,
  },
  "bottom-right": {
    bottom: isMobile ? 14 : 22,
    right: isMobile ? 12 : 20,
  },
});

const getNearestSubscriptionExpiryCorner = (rect, isMobile, topNoticeHeight) => {
  if (typeof window === "undefined") {
    return getDefaultSubscriptionExpiryCorner();
  }

  const offsets = getSubscriptionExpiryOffsets(isMobile, topNoticeHeight);
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = rect?.width || (isMobile ? 240 : 320);
  const height = rect?.height || 42;
  const currentPoint = {
    x: rect?.left ?? viewportWidth - width - (offsets["top-right"].right || 0),
    y: rect?.top ?? (offsets["top-right"].top || 0),
  };

  const cornerPoints = {
    "top-left": {
      x: offsets["top-left"].left || 0,
      y: offsets["top-left"].top || 0,
    },
    "top-right": {
      x: viewportWidth - width - (offsets["top-right"].right || 0),
      y: offsets["top-right"].top || 0,
    },
    "bottom-left": {
      x: offsets["bottom-left"].left || 0,
      y: viewportHeight - height - (offsets["bottom-left"].bottom || 0),
    },
    "bottom-right": {
      x: viewportWidth - width - (offsets["bottom-right"].right || 0),
      y: viewportHeight - height - (offsets["bottom-right"].bottom || 0),
    },
  };

  return Object.entries(cornerPoints).reduce(
    (closestCorner, [corner, point]) => {
      const distance = Math.hypot(point.x - currentPoint.x, point.y - currentPoint.y);
      if (distance < closestCorner.distance) {
        return { corner, distance };
      }
      return closestCorner;
    },
    { corner: getDefaultSubscriptionExpiryCorner(), distance: Number.POSITIVE_INFINITY }
  ).corner;
};

const DefaultLayouts = ({ isLoading, ...props }) => {
  const auth = useContext(AuthContext);
  const [layoutLoader, setLayoutLoader] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const isExpanded = auth.isExpanded;
  const classes = useStyles();
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [change, setChange] = useState(false);
  const [onboardingWidgetCorner, setOnboardingWidgetCorner] = useState(
    getDefaultOnboardingWidgetCorner
  );
  const [subscriptionExpiryCorner, setSubscriptionExpiryCorner] = useState(
    getDefaultSubscriptionExpiryCorner
  );
  const [onboardingWidgetDragStyle, setOnboardingWidgetDragStyle] = useState(null);
  const [subscriptionExpiryDragStyle, setSubscriptionExpiryDragStyle] = useState(null);
  const [isDraggingOnboardingWidget, setIsDraggingOnboardingWidget] = useState(false);
  const [isDraggingSubscriptionExpiry, setIsDraggingSubscriptionExpiry] = useState(false);
  const [suppressOnboardingWidgetClick, setSuppressOnboardingWidgetClick] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isInvoiceCreateRoute =
    location.pathname === "/invoicing/create" || location.pathname === "/invoices/create";
  const isInvoiceBuilderRoute =
    location.pathname === "/invoicing/create" ||
    location.pathname === "/invoices/create" ||
    /^\/invoicing\/[^/]+\/edit$/.test(location.pathname);
  const onboardingWidgetRef = useRef(null);
  const onboardingWidgetDragRef = useRef(null);
  const subscriptionExpiryRef = useRef(null);
  const subscriptionExpiryDragRef = useRef(null);
  const topNoticeBarRef = useRef(null);
  const appHeaderRef = useRef(null);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const { stopTour } = useTour();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [topNoticeHeight, setTopNoticeHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const {
    isModalOpen: isOnboardingModalOpen,
    shouldShowIndicator: shouldShowHeaderOnboardingIndicator,
    showCompletionIndicator,
  } = useOnboardingExperience();
  const isCompanyUser = auth?.userProfile?.role === "companyUser";
  const subscriptionStatusRaw = String(auth?.userProfile?.subscriptionDetails?.status || "").toLowerCase();
  const isTrialSubscription =
    subscriptionStatusRaw === "trialing" || subscriptionStatusRaw === "trial";
  const userPromoCode =
    auth?.userProfile?.userPromoCode ||
    auth?.userProfile?.promoCode ||
    auth?.userProfile?.subscriptionDetails?.promoCode ||
    "";
  const promoUpper = String(userPromoCode).trim().toUpperCase();
  const isPartnerPromoUser = promoUpper.startsWith("FP");
  const isPreferredPartner =
    auth?.userProfile?.subscriptionDetails?.isFullDiscount === true ||
    auth?.userProfile?.isFullDiscount === true ||
    promoUpper.startsWith("FP26");
  /** Partner / full-discount accounts: no trial banner; payment banner when no card. */
  const hideTrialBannerForPartner = isPreferredPartner || isPartnerPromoUser;
  const isFreemiumPlan = useMemo(() => {
    const name = String(auth?.userProfile?.subscriptionDetails?.plan?.name || "").toLowerCase();
    return name === "freemium";
  }, [auth?.userProfile?.subscriptionDetails?.plan?.name]);
  const hasResolvedPaymentMethods = auth?.userProfile?.cardsDataLoaded === true;
  const hasNoPaymentMethod =
    isCompanyUser &&
    hasResolvedPaymentMethods &&
    Array.isArray(auth?.userProfile?.cardsData?.cards) &&
    auth?.userProfile?.cardsData?.cards.length === 0;
  const trialDaysRemainingForBillingPopup = useMemo(() => {
    const trialEnd = auth?.userProfile?.subscriptionDetails?.current_period_end;

    if (!trialEnd || Number.isNaN(Number(trialEnd))) {
      return null;
    }

    const today = new Date();
    const endDate = new Date(Number(trialEnd) * 1000);
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  }, [auth?.userProfile?.subscriptionDetails?.current_period_end]);
  const shouldShowSidebarOnboardingIndicator =
    !isOnboardingModalOpen &&
    !isExpanded &&
    !change &&
    (shouldShowHeaderOnboardingIndicator || showCompletionIndicator);
  const trialBannerInfo = useMemo(() => {
    if (!auth?.status || !isCompanyUser || !isTrialSubscription || hideTrialBannerForPartner || isFreemiumPlan) {
      return null;
    }

    const trialEndRaw = auth?.userProfile?.subscriptionDetails?.current_period_end;
    if (!trialEndRaw || Number.isNaN(Number(trialEndRaw))) {
      return null;
    }

    const today = new Date();
    const trialEndDate = new Date(Number(trialEndRaw) * 1000);
    today.setHours(0, 0, 0, 0);
    trialEndDate.setHours(0, 0, 0, 0);

    const dayDiff = Math.ceil((trialEndDate - today) / (1000 * 60 * 60 * 24));
    const isExpired = dayDiff < 0;
    const displayDays = isExpired ? 0 : dayDiff;

    return {
      dayDiff,
      displayDays,
      isExpired,
      endsOnText: trialEndDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    };
  }, [
    auth?.status,
    auth?.userProfile?.subscriptionDetails?.current_period_end,
    isCompanyUser,
    isTrialSubscription,
    hideTrialBannerForPartner,
    isFreemiumPlan,
  ]);
  const showTrialBanner = Boolean(trialBannerInfo);
  const isTrialWithinPaymentBannerWindow =
    trialDaysRemainingForBillingPopup !== null &&
    trialDaysRemainingForBillingPopup <= BILLING_POPUP_VISIBLE_DAYS &&
    trialDaysRemainingForBillingPopup >= 0;
  const showPaymentBanner =
    hasNoPaymentMethod &&
    !isFreemiumPlan &&
    (!isTrialSubscription || isTrialWithinPaymentBannerWindow);
  /** Legacy names — keep defined so older bundles / stray refs do not throw */
  const SubBar = showTrialBanner;
  const cardBar = showPaymentBanner;
  const shouldHideTopBillingBars = isInvoiceBuilderRoute;
  const showTopBillingBar =
    !shouldHideTopBillingBars && (showTrialBanner || showPaymentBanner);

  const subscriptionExpiryInfo = useMemo(() => {
    if (!auth?.status || !auth?.userProfile) {
      return null;
    }

    const subscriptionStatus = String(auth?.userProfile?.subscriptionDetails?.status || "").toLowerCase();
    const isTrialStatus = subscriptionStatus === "trialing" || subscriptionStatus === "trial";

    if (!isCompanyUser || isTrialStatus || isPartnerPromoUser || isFreemiumPlan) {
      return null;
    }

    const subscriptionEndDateRaw =
      auth?.userProfile?.subscription_end_date ||
      auth?.userProfile?.subscriptionDetails?.subscription_end_date ||
      auth?.userProfile?.subscriptionDetails?.current_period_end;

    if (!subscriptionEndDateRaw) {
      return null;
    }

    let endDate;
    if (typeof subscriptionEndDateRaw === "number") {
      endDate = new Date(subscriptionEndDateRaw * 1000);
    } else {
      endDate = new Date(subscriptionEndDateRaw);
    }

    if (Number.isNaN(endDate.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const dayDiff = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    const isExpired = dayDiff < 0;
    const absDays = Math.abs(dayDiff);
    const shouldShow = isExpired || dayDiff <= SUBSCRIPTION_EXPIRY_VISIBLE_DAYS;

    if (!shouldShow) {
      return null;
    }

    return {
      isExpired,
      dayDiff,
      absDays,
      text: isExpired
        ? `Expired ${absDays} day${absDays !== 1 ? "s" : ""} ago`
        : `Expires in ${dayDiff} day${dayDiff !== 1 ? "s" : ""}`,
    };
  }, [auth?.status, auth?.userProfile, isCompanyUser, isPartnerPromoUser, isFreemiumPlan]);

  const paymentTrialEndLabel = useMemo(() => {
    const raw = auth?.userProfile?.subscriptionDetails?.current_period_end;
    if (!raw || Number.isNaN(Number(raw))) {
      return null;
    }
    const d = new Date(Number(raw) * 1000);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, [auth?.userProfile?.subscriptionDetails?.current_period_end]);

  useEffect(() => {
    setOnboardingWidgetCorner(readSavedOnboardingWidgetCorner());
    setSubscriptionExpiryCorner(readSavedSubscriptionExpiryCorner());
  }, []);

  useEffect(() => {
    const darkModePreference = localStorage?.getItem('darkMode') === 'true';
    setIsDarkMode(darkModePreference);
  }, []);

  // Keep drawer/main offset in sync with the fixed billing banner. Must re-run when the banner
  // is shown/hidden (e.g. route changes to/from invoice builder), not only when trial/card flags change.
  useLayoutEffect(() => {
    if (!showTopBillingBar) {
      setTopNoticeHeight(0);
      return;
    }

    const node = topNoticeBarRef.current;
    if (!node) {
      setTopNoticeHeight(0);
      return;
    }

    const measure = () => {
      const h = node.offsetHeight;
      setTopNoticeHeight(Number.isFinite(h) ? h : 0);
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    window.addEventListener("resize", measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [showTopBillingBar, isMobile]);

  useLayoutEffect(() => {
    if (auth?.status !== true || isInvoiceBuilderRoute || isExpanded) {
      setHeaderHeight(0);
      return;
    }

    const node = appHeaderRef.current;
    if (!node) {
      setHeaderHeight(0);
      return;
    }

    const measure = () => {
      const h = node.offsetHeight;
      setHeaderHeight(Number.isFinite(h) ? h : 0);
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    window.addEventListener("resize", measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [auth?.status, isExpanded, isInvoiceBuilderRoute, isMobile, change]);

  // Only show the global overlay while auth is still resolving. After login, keep the shell
  // visible (no gray flash) while background data loads — avoids flicker login → dashboard.
  const isAuthLoading = isLoading === true && auth?.status !== true;
  useEffect(() => {
    if (!isAuthLoading) {
      setShowLoader(false);
      return;
    }
    const timer = setTimeout(() => setShowLoader(true), LOADER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isAuthLoading]);

  useEffect(() => {
    setLayoutLoader(false);
  }, [location.pathname]);

  /** Expired trial: send users to plan page once when landing on home dashboard (banner still shows everywhere). */
  useEffect(() => {
    if (auth?.status !== true || !isCompanyUser || isFreemiumPlan || !isTrialSubscription) {
      return;
    }
    if (!trialBannerInfo?.isExpired) {
      return;
    }
    const path = location.pathname;
    if (path !== "/dashboard" && path !== "/") {
      return;
    }
    if (sessionStorage.getItem("trialExpiredDefaultPlan") === "1") {
      return;
    }
    sessionStorage.setItem("trialExpiredDefaultPlan", "1");
    navigate("/account/upgrade", { replace: true });
  }, [
    auth?.status,
    isCompanyUser,
    isFreemiumPlan,
    isTrialSubscription,
    trialBannerInfo?.isExpired,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    setOnboardingWidgetDragStyle(null);
    setIsDraggingOnboardingWidget(false);
    setSuppressOnboardingWidgetClick(false);
    onboardingWidgetDragRef.current = null;
    setSubscriptionExpiryDragStyle(null);
    setIsDraggingSubscriptionExpiry(false);
    subscriptionExpiryDragRef.current = null;
  }, [location.pathname, isMobile]);

  const stopOnboardingWidgetDrag = useCallback((clientX, clientY) => {
    const dragState = onboardingWidgetDragRef.current;
    if (!dragState) {
      return;
    }

    onboardingWidgetDragRef.current = null;

    if (!dragState.didMove) {
      setIsDraggingOnboardingWidget(false);
      return;
    }

    const widgetRect = onboardingWidgetRef.current?.getBoundingClientRect();
    const fallbackRect = {
      left: clientX - dragState.pointerOffsetX,
      top: clientY - dragState.pointerOffsetY,
      width: widgetRect?.width,
      height: widgetRect?.height,
    };
    const nextCorner = getNearestOnboardingWidgetCorner(widgetRect || fallbackRect, isMobile);

    setOnboardingWidgetCorner(nextCorner);
    saveOnboardingWidgetCorner(nextCorner);
    setOnboardingWidgetDragStyle(null);
    setIsDraggingOnboardingWidget(false);
    setSuppressOnboardingWidgetClick(true);

    window.setTimeout(() => {
      setSuppressOnboardingWidgetClick(false);
    }, 180);
  }, [isMobile]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const dragState = onboardingWidgetDragRef.current;
      if (!dragState) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      if (!dragState.didMove && Math.hypot(deltaX, deltaY) < 6) {
        return;
      }

      onboardingWidgetDragRef.current = {
        ...dragState,
        didMove: true,
      };

      setIsDraggingOnboardingWidget(true);
      setSuppressOnboardingWidgetClick(true);
      setOnboardingWidgetDragStyle({
        left: Math.max(12, event.clientX - dragState.pointerOffsetX),
        top: Math.max(12, event.clientY - dragState.pointerOffsetY),
      });
    };

    const handleTouchMove = (event) => {
      const dragState = onboardingWidgetDragRef.current;
      const touch = event.touches?.[0];

      if (!dragState || !touch) {
        return;
      }

      const deltaX = touch.clientX - dragState.startX;
      const deltaY = touch.clientY - dragState.startY;

      if (!dragState.didMove && Math.hypot(deltaX, deltaY) < 6) {
        return;
      }

      onboardingWidgetDragRef.current = {
        ...dragState,
        didMove: true,
      };

      setIsDraggingOnboardingWidget(true);
      setSuppressOnboardingWidgetClick(true);
      setOnboardingWidgetDragStyle({
        left: Math.max(12, touch.clientX - dragState.pointerOffsetX),
        top: Math.max(12, touch.clientY - dragState.pointerOffsetY),
      });

      event.preventDefault();
    };

    const handleMouseUp = (event) => {
      stopOnboardingWidgetDrag(event.clientX, event.clientY);
    };

    const handleTouchEnd = (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch) {
        stopOnboardingWidgetDrag(0, 0);
        return;
      }
      stopOnboardingWidgetDrag(touch.clientX, touch.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [stopOnboardingWidgetDrag]);

  const stopSubscriptionExpiryDrag = useCallback((clientX, clientY) => {
    const dragState = subscriptionExpiryDragRef.current;
    if (!dragState) {
      return;
    }

    subscriptionExpiryDragRef.current = null;

    if (!dragState.didMove) {
      setIsDraggingSubscriptionExpiry(false);
      return;
    }

    const badgeRect = subscriptionExpiryRef.current?.getBoundingClientRect();
    const fallbackRect = {
      left: clientX - dragState.pointerOffsetX,
      top: clientY - dragState.pointerOffsetY,
      width: badgeRect?.width,
      height: badgeRect?.height,
    };
    const nextCorner = getNearestSubscriptionExpiryCorner(
      badgeRect || fallbackRect,
      isMobile,
      topNoticeHeight
    );

    setSubscriptionExpiryCorner(nextCorner);
    saveSubscriptionExpiryCorner(nextCorner);
    setSubscriptionExpiryDragStyle(null);
    setIsDraggingSubscriptionExpiry(false);
  }, [isMobile, topNoticeHeight]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const dragState = subscriptionExpiryDragRef.current;
      if (!dragState) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      if (!dragState.didMove && Math.hypot(deltaX, deltaY) < 6) {
        return;
      }

      subscriptionExpiryDragRef.current = {
        ...dragState,
        didMove: true,
      };

      setIsDraggingSubscriptionExpiry(true);
      setSubscriptionExpiryDragStyle({
        left: Math.max(12, event.clientX - dragState.pointerOffsetX),
        top: Math.max(12, event.clientY - dragState.pointerOffsetY),
      });
    };

    const handleTouchMove = (event) => {
      const dragState = subscriptionExpiryDragRef.current;
      const touch = event.touches?.[0];

      if (!dragState || !touch) {
        return;
      }

      const deltaX = touch.clientX - dragState.startX;
      const deltaY = touch.clientY - dragState.startY;

      if (!dragState.didMove && Math.hypot(deltaX, deltaY) < 6) {
        return;
      }

      subscriptionExpiryDragRef.current = {
        ...dragState,
        didMove: true,
      };

      setIsDraggingSubscriptionExpiry(true);
      setSubscriptionExpiryDragStyle({
        left: Math.max(12, touch.clientX - dragState.pointerOffsetX),
        top: Math.max(12, touch.clientY - dragState.pointerOffsetY),
      });

      event.preventDefault();
    };

    const handleMouseUp = (event) => {
      stopSubscriptionExpiryDrag(event.clientX, event.clientY);
    };

    const handleTouchEnd = (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch) {
        stopSubscriptionExpiryDrag(0, 0);
        return;
      }
      stopSubscriptionExpiryDrag(touch.clientX, touch.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [stopSubscriptionExpiryDrag]);

  const handleSubscriptionExpiryDragStart = useCallback((clientX, clientY) => {
    const badgeRect = subscriptionExpiryRef.current?.getBoundingClientRect();

    if (!badgeRect) {
      return;
    }

    subscriptionExpiryDragRef.current = {
      startX: clientX,
      startY: clientY,
      pointerOffsetX: clientX - badgeRect.left,
      pointerOffsetY: clientY - badgeRect.top,
      didMove: false,
    };
  }, []);

  const handleSubscriptionExpiryMouseDownCapture = useCallback((event) => {
    handleSubscriptionExpiryDragStart(event.clientX, event.clientY);
  }, [handleSubscriptionExpiryDragStart]);

  const handleSubscriptionExpiryTouchStartCapture = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }
    handleSubscriptionExpiryDragStart(touch.clientX, touch.clientY);
  }, [handleSubscriptionExpiryDragStart]);

  const handleOnboardingWidgetDragStart = useCallback((clientX, clientY) => {
    const widgetRect = onboardingWidgetRef.current?.getBoundingClientRect();

    if (!widgetRect) {
      return;
    }

    onboardingWidgetDragRef.current = {
      startX: clientX,
      startY: clientY,
      pointerOffsetX: clientX - widgetRect.left,
      pointerOffsetY: clientY - widgetRect.top,
      didMove: false,
    };
  }, []);

  const handleOnboardingWidgetMouseDownCapture = useCallback((event) => {
    handleOnboardingWidgetDragStart(event.clientX, event.clientY);
  }, [handleOnboardingWidgetDragStart]);

  const handleOnboardingWidgetTouchStartCapture = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }
    handleOnboardingWidgetDragStart(touch.clientX, touch.clientY);
  }, [handleOnboardingWidgetDragStart]);

  useEffect(() => {
    if (!isCompanyUser) {
      setOpenPaymentDialog(false);
      return;
    }

    const isNearTrialExpiry =
      trialDaysRemainingForBillingPopup !== null &&
      trialDaysRemainingForBillingPopup <= BILLING_POPUP_VISIBLE_DAYS &&
      trialDaysRemainingForBillingPopup >= 0;
    const isNearSubscriptionExpiry =
      !!subscriptionExpiryInfo &&
      !subscriptionExpiryInfo.isExpired &&
      subscriptionExpiryInfo.dayDiff <= BILLING_POPUP_VISIBLE_DAYS &&
      subscriptionExpiryInfo.dayDiff >= 0;
    const popupClosedInSession = localStorage.getItem(BILLING_POPUP_CLOSED_KEY) === "true";
    const trialEligibleForBillingPrompt =
      isTrialSubscription && !hideTrialBannerForPartner && isNearTrialExpiry;
    setOpenPaymentDialog(
      !isFreemiumPlan &&
      hasNoPaymentMethod &&
      (trialEligibleForBillingPrompt ||
        (!isTrialSubscription && isNearSubscriptionExpiry)) &&
      !popupClosedInSession
    );
  }, [
    auth?.status,
    auth?.userProfile,
    hasNoPaymentMethod,
    isCompanyUser,
    isTrialSubscription,
    hideTrialBannerForPartner,
    isFreemiumPlan,
    trialDaysRemainingForBillingPopup,
    subscriptionExpiryInfo,
  ]);

  const logoutuser = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    // Trigger logout immediately so UI reacts fast
    auth.logoutuser();
    stopTour();
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('clientId')
    localStorage.removeItem('companyId')
    localStorage.removeItem('clients')
    localStorage.removeItem('companies')
    localStorage.removeItem(BILLING_POPUP_CLOSED_KEY);
    try {
      sessionStorage.removeItem("trialExpiredDefaultPlan");
    } catch (_e) { /* ignore */ }
    localStorage.setItem('darkMode', 'false');

    // Run non-critical data update in background
    if (auth?.userProfile?.role === "companyUser") {
      try {
        const data = { hideAddPaymentPopup: false }
        setAnyUserData(auth?.userProfile?.id, data).catch(err => console.error("Non-critical logout update failed", err));
      } catch (_err) { /* ignore */ }
    }
  };

  const handleClick = () => {
    navigate("/account/profile");
  };

  useEffect(() => {
    const path = location.pathname;
    if (path === "/dashboard" || path === "/account/profile") {
      setMobileOpen(false);
    }
  }, [location.pathname]);

  const isDeletionScheduled = auth?.userProfile?.companyData && auth?.userProfile?.companyData?.data?.delete === true;

  const toggleDarkMode = (darkMode) => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
    setIsDarkMode(darkMode);
  };

  const Role =
    auth?.userProfile?.role === "companyUser"
      ? "Company Manager"
      : (Array.isArray(auth?.userProfile?.role) ? auth?.userProfile?.role.includes("admin") : auth?.userProfile?.role === "admin")
        ? "Sys. Admin"
        : (Array.isArray(auth?.userProfile?.role) ? auth?.userProfile?.role.includes("Employee") : auth?.userProfile?.role === "Employee")
          ? auth?.userProfile?.employeerole === "Admin"
            ? "Team Admin"
            : auth?.userProfile?.employeerole
          : auth?.userProfile?.role;

  const drawer = (
    <div
      className={`${isMobile
        ? ""
        : isExpanded
          ? ""
          : change
            ? "drawerChangeWidth"
            : "drawerWidth"
        }`}
    >
      {auth?.status !== true && <Navigate to="/login" />}
      <div className={classes.toolbar} />
      <div
        className="d-flex flex-direction-column position-relative my-scroll"
        style={{
          height: "100%",
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
          paddingBottom: isMobile
            ? shouldShowSidebarOnboardingIndicator
              ? "190px"
              : "120px"
            : shouldShowSidebarOnboardingIndicator
              ? "240px"
              : "150px",
        }}
      >
        {isMobile && (
          <div className="pv-10 ph-15 profile  dark:bg-[#1A1F2C]">
            <Link to="/account/profile" className="d-flex flex-center">
              <div className="mr-10">
                <ListItemAvatar style={{ minWidth: "20px" }}>
                  <Avatar
                    variant="circular"
                    className="circularName"
                    alt="Remy Sharp"
                  >{`${auth?.userProfile?.firstName?.[0] ?? ""}${auth?.userProfile?.lastName?.[0] ?? ""
                    }`}</Avatar>
                </ListItemAvatar>
              </div>
              <div className="nameProfile">
                {`${auth.userProfile?.firstName} ${auth.userProfile?.lastName}`}
              </div>
            </Link>
          </div>
        )}
        <div className={isDeletionScheduled ? "pointer-events-none opacity-60" : ""}>
          <Suspense fallback={null}>
            <SidebarMenu
              handleDrawerToggle={handleDrawerToggle}
              change={change}
              setChange={setChange}
            />
          </Suspense>
        </div>
        {shouldShowSidebarOnboardingIndicator && (
            <div
              style={{
                bottom: isMobile ? "112px" : "145px",
                left: isMobile ? "10px" : "5px",
                position: "fixed",
                width: isMobile ? "214px" : "221px",
                zIndex: 1203,
              }}
            >
              <NavbarProgressIndicator
                variant="sidebar"
                onOpen={() => {
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
              />
            </div>
          )}
        {!isExpanded && !change ? (
          <div className={isMobile ? "py-2 w-[230px] border-b border-slate-700 d-flex flex-center justify-between pl-12 dark:bg-[#1A1F2C]" : "py-2 w-[245px] mb-16 border-b border-slate-700 d-flex flex-center justify-between pl-12 dark:bg-[#1A1F2C]"}
            style={{
              bottom: isMobile ? "50px" : "68px",
              position: "fixed",
              cursor: "pointer",
              height: "50px",
              borderTop: "1px solid #334155",
              backgroundColor: !isDarkMode ? "#2C3440" : "",
            }}>
            <div className="flex items-center justify-between">
              {isDarkMode ? <Moon size={20} color="white" /> : <Sun size={20} color="white" />}
              {<span className="ml-8" style={{ fontSize: "16px", color: "white" }}>Dark Mode</span>}
            </div>
            {<Switch checked={isDarkMode} onCheckedChange={() => toggleDarkMode(!isDarkMode)} />}
          </div>
        ) : (null)}
        {change && (
          <div className="pl-5" style={{
            bottom: "30px",
            position: "fixed",
            cursor: "pointer",
            height: "50px",
            backgroundColor: !isDarkMode ? "#2C3440" : "",
          }}>
            <Switch checked={isDarkMode} onCheckedChange={() => toggleDarkMode(!isDarkMode)} />
          </div>
        )}
        {!isMobile && !isExpanded && !change ? (
          <>
            <div
              className="d-flex flex-center pl-12 logoutLink dark:bg-[#1A1F2C]"
              style={{
                bottom: "35px",
                position: "fixed",
                cursor: "pointer",
                height: "auto", // 50px
                borderTop: "1px solid #334155",
                backgroundColor: !isDarkMode ? "#2C3440" : "",
                paddingTop: "10px",
                paddingBottom: "12px"
              }}
              onClick={["admin", "Company Manager", "Client"].includes(Role) || ((Array.isArray(auth?.userProfile?.role) ? auth?.userProfile?.role.includes("Employee") : auth?.userProfile?.role === "Employee") && auth?.userProfile?.employeerole === "Admin") ? handleClick : null}
            >
              <ListItemAvatar style={{ minWidth: "35px" }}>
                <Avatar
                  variant="circular"
                  className="circularName"
                  alt="Remy Sharp"
                >{`${auth?.userProfile?.firstName?.[0] ?? ""}${auth?.userProfile?.lastName?.[0] ?? ""
                  }`}</Avatar>
              </ListItemAvatar>
              <Typography component="div" style={{ fontSize: "0.85rem", lineHeight: "1.2" }} className={["admin", "Company Manager", "Client"].includes(Role) ? "color-white" : "color-white"}>
                <div>
                  <span className="text-underLine">{`${auth?.userProfile?.firstName} ${auth?.userProfile?.lastName}`}</span>
                  <span className="block">{`(${Role})`}</span>
                </div>
              </Typography>
            </div>
          </>
        ) : null}
        {isExpanded && !isMobile ? null : (
          <>
            <div
              style={{ backgroundColor: !isDarkMode ? "#2C3440" : "" }}
              className={`${classes.logoutLink}
          ${isMobile
                  ? "pl-10 logoutLink dark:bg-[#1A1F2C]"
                  : !isExpanded && change
                    ? "drawerChangeWidth"
                    : isExpanded
                      ? ""
                      : "pl-10 logoutLink dark:bg-[#1A1F2C]"
                }`}
            >
              <Button
                className={`${classes.btnLogout} ${isExpanded
                  ? "collapsed"
                  : !isExpanded && change
                    ? "logoutMinWidth pv-6 ph-20"
                    : ""
                  }`}
                variant="text"
                onClick={logoutuser}
                startIcon={isExpanded && !isMobile ? null : <ExitToAppIcon />}
              >
                {(isExpanded || change) && !isMobile ? null : "Logout"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const floatingIndicatorBottom = isMobile ? "14px" : "22px";
  const showFixedHeader = auth?.status === true && !isExpanded && !isInvoiceBuilderRoute;
  const effectiveTopNoticeHeight = showTopBillingBar ? topNoticeHeight : 0;
  const shellTopOffset = isMobile
    ? effectiveTopNoticeHeight + (showFixedHeader ? headerHeight : 0)
    : effectiveTopNoticeHeight;
  const drawerPaperOffsetStyle = {
    marginTop: `${effectiveTopNoticeHeight}px`,
    height: `calc(100vh - ${effectiveTopNoticeHeight}px)`,
  };

  const handleClosePaymentDialog = async () => {
    localStorage.setItem(BILLING_POPUP_CLOSED_KEY, "true");
    setOpenPaymentDialog(false);

    const data = { hideAddPaymentPopup: true }
    await setAnyUserData(auth?.userProfile?.id, data);
  };

  const handleReEngage = async () => {
    try {
      await updateSettings(
        {
          delete: false,
          deletedAt: null,
          deletedBy: null,
          reEngagedAt: new Date(),
        },
        "users",
        auth?.userProfile?.id
      );

      await logoutuser();
    } catch (error) {
      console.error("Re-engage failed", error);
    }
  };


  return (
    <>

      {auth?.userProfile && isDeletionScheduled && (
        <ReEngageModal
          open={isDeletionScheduled}
          onConfirm={handleReEngage}
          onLogout={logoutuser}
          auth={auth?.userProfile}
        />
      )}

      {/* Session timeout warning — visible to all authenticated users */}
      <SessionWarningModal open={!!auth?.sessionWarning} />
      <OnboardingModal />
      {!isOnboardingModalOpen && (
        <div
          ref={onboardingWidgetRef}
          style={{
            position: "fixed",
            ...(onboardingWidgetDragStyle ||
              getOnboardingWidgetOffsets(isMobile)[onboardingWidgetCorner] || {
                bottom: floatingIndicatorBottom,
                right: isMobile ? "12px" : "20px",
              }),
            width: isMobile ? "min(240px, calc(100vw - 24px))" : "260px",
            maxWidth: isMobile ? "240px" : "260px",
            zIndex: 1204,
            cursor: isDraggingOnboardingWidget ? "grabbing" : "grab",
            userSelect: "none",
            touchAction: "none",
            transform: isDraggingOnboardingWidget ? "scale(1.01)" : "none",
            transition: onboardingWidgetDragStyle ? "none" : "box-shadow 0.2s ease, transform 0.2s ease",
          }}
        >
          <NavbarProgressIndicator
            suppressClick={suppressOnboardingWidgetClick}
            isDragging={isDraggingOnboardingWidget}
            onMouseDownCapture={handleOnboardingWidgetMouseDownCapture}
            onTouchStartCapture={handleOnboardingWidgetTouchStartCapture}
            onOpen={() => {
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
          />
        </div>
      )}

      {showTopBillingBar && (
        <div
          ref={topNoticeBarRef}
          style={{
            zIndex: 1000,
            position: "fixed",
            width: "100%",
            top: 0,
            left: 0,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 1px 0 rgba(15, 23, 42, 0.06)",
          }}
        >
          {showTrialBanner && trialBannerInfo && (
            <div
              className="d-flex flex-center"
              style={{
                flexWrap: "nowrap",
                justifyContent: "center",
                gap: isMobile ? 8 : 6,
                padding: isMobile ? "8px 12px" : "10px 20px",
                backgroundColor: isDarkMode ? "#451a03" : "#FFFBEB",
                borderBottom: showPaymentBanner
                  ? isDarkMode
                    ? "1px solid #92400e"
                    : "1px solid #FCD34D"
                  : "none",
                fontSize: 14,
                lineHeight: "20px",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <WarningAmberOutlinedIcon
                fontSize="small"
                style={{ color: isDarkMode ? "#FBBF24" : "#D97706", flexShrink: 0 }}
              />
              <span
                style={{
                  color: isDarkMode ? "#FEF3C7" : "#020817",
                  fontWeight: 500,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {trialBannerInfo.isExpired ? (
                  <>
                    Your trial has expired.{" "}
                    <Link
                      to="/account/upgrade"
                      style={{
                        color: isDarkMode ? "#86EFAC" : "#16A34A",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      Please choose a plan to continue
                    </Link>
                  </>
                ) : (
                  <>
                    You’re on a full-access trial with{" "}
                    <strong style={{ fontWeight: 700 }}>{trialBannerInfo.displayDays}</strong>{" "}
                    day{trialBannerInfo.displayDays !== 1 ? "s" : ""} remaining.
                  </>
                )}
              </span>
            </div>
          )}

          {showPaymentBanner && (
            <div
              className="d-flex flex-center"
              style={{
                flexWrap: "wrap",
                justifyContent: "center",
                gap: isMobile ? 8 : 6,
                padding: "12px 20px",
                backgroundColor: isDarkMode ? "#252B38" : "#EEF2FF",
                borderBottom: isDarkMode ? "1px solid #334155" : "1px solid #A5B4FC",
                fontSize: 14,
                lineHeight: "20px",
              }}
            >
              <CreditCardOutlinedIcon
                fontSize="small"
                style={{ color: isDarkMode ? "#94A3B8" : "#4338CA", flexShrink: 0 }}
              />
              <span
                style={{
                  color: isDarkMode ? "#E2E8F0" : "#1E1B4B",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {isTrialSubscription && paymentTrialEndLabel ? (
                  <>
                    Add a{" "}
                    <Link
                      to="/account/companySubscription"
                      style={{
                        color: isDarkMode ? "#38BDF8" : "#2563EB",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      payment method
                    </Link>{" "}
                    before your trial ends on {paymentTrialEndLabel}.
                  </>
                ) : (
                  <>
                    <Link
                      to="/account/companySubscription"
                      style={{
                        color: isDarkMode ? "#38BDF8" : "#2563EB",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      Add a payment method
                    </Link>{" "}
                    to keep your subscription active.
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      )}
      {!shouldHideTopBillingBars && subscriptionExpiryInfo && (
        <div
          ref={subscriptionExpiryRef}
          onMouseDownCapture={handleSubscriptionExpiryMouseDownCapture}
          onTouchStartCapture={handleSubscriptionExpiryTouchStartCapture}
          style={{
            position: "fixed",
            ...(subscriptionExpiryDragStyle ||
              getSubscriptionExpiryOffsets(isMobile, effectiveTopNoticeHeight)[subscriptionExpiryCorner]),
            zIndex: 1202,
            maxWidth: isMobile ? "calc(100vw - 24px)" : "320px",
            width: "fit-content",
            backgroundColor: subscriptionExpiryInfo.isExpired
              ? "#FEE2E2"
              : subscriptionExpiryInfo.dayDiff <= 7
                ? "#FFFBEB"
                : "#E0E7FF",
            color: subscriptionExpiryInfo.isExpired
              ? "#B91C1C"
              : subscriptionExpiryInfo.dayDiff <= 7
                ? "#B45309"
                : "#1E3A8A",
            border: `1px solid ${
              subscriptionExpiryInfo.isExpired
                ? "#FCA5A5"
                : subscriptionExpiryInfo.dayDiff <= 7
                  ? "#FCD34D"
                  : "#A5B4FC"
            }`,
            borderRadius: "999px",
            padding: isMobile ? "8px 12px" : "8px 14px",
            fontSize: "13px",
            fontWeight: 600,
            lineHeight: "18px",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.12)",
            textAlign: "center",
            cursor: isDraggingSubscriptionExpiry ? "grabbing" : "grab",
            userSelect: "none",
            touchAction: "none",
            transform: isDraggingSubscriptionExpiry ? "scale(1.01)" : "none",
            transition: subscriptionExpiryDragStyle
              ? "none"
              : "box-shadow 0.2s ease, transform 0.2s ease",
          }}
        >
          {subscriptionExpiryInfo.text}
        </div>
      )}
      {auth?.userProfile?.role === "companyUser" && openPaymentDialog && (
        <CommonConfirmationDialog
          open={openPaymentDialog}
          onConfirm={() => {
            localStorage.setItem(BILLING_POPUP_CLOSED_KEY, "true");
            setOpenPaymentDialog(false);
            navigate("/account/companySubscription");
          }}
          onClose={handleClosePaymentDialog}
          title="Complete Your Billing Setup"
          message="Please add a payment method to avoid service interruption when your trial ends."
          buttonTitle="Add Payment Method"
        />
      )}

      <CommonContext.Provider
        value={{
          loader: layoutLoader,
          setLoader: setLayoutLoader,
        }}
      >
        <div className={`DefaultContainer min-h-screen dark:bg-[#1A1F2C]`}>
          <div className={classes.root}>
            <CssBaseline />
            {showFixedHeader && (
              <AppBar
                ref={appHeaderRef}
                position="fixed"
                className={`${classes.appBar} pr-0 dark:bg-[#1A1F2C]`}
                style={{
                  width: !isMobile ? (change ? "55px" : "250px") : "",
                  marginTop: `${effectiveTopNoticeHeight}px`,
                  right: !isExpanded && !isMobile ? "inherit" : 0,
                }}
              >
                <Toolbar
                  className={classes.toolbar}
                  style={{
                    minHeight: !isMobile ? "" : "40px",
                    paddingLeft: change ? 0 : undefined,
                    paddingRight: change ? 0 : undefined,
                    justifyContent: change ? "center" : undefined,
                  }}
                >
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    className={classes.menuButton}
                  >
                    <MenuIcon fontSize="large" />
                  </IconButton>
                  <Suspense fallback={null}>
                    <Header
                      history={props.history}
                      setChange={setChange}
                      change={change}
                    />
                  </Suspense>
                </Toolbar>
              </AppBar>
            )}
            {auth?.status === true && !isInvoiceBuilderRoute && (
              <>
                <nav
                  className={`${classes.drawer} ${isMobile
                    ? ""
                    : isExpanded
                      ? ""
                      : change
                        ? "drawerChangeWidth"
                        : "drawerWidth"
                    }`}
                  aria-label=" folders"
                >
                  <Hidden smUp implementation="css">
                    <Drawer
                      variant="temporary"
                      anchor={theme.direction === "rtl" ? "right" : "left"}
                      open={mobileOpen}
                      onClose={handleDrawerToggle}
                      classes={{
                        paper: isDarkMode ? classes.drawerPaperDark : classes.drawerPaper,
                      }}
                      PaperProps={{
                        style: drawerPaperOffsetStyle,
                      }}
                      ModalProps={{
                        keepMounted: true,
                      }}
                    // style={{ backgroundColor: !isDarkMode ? "#2C3440" : "" }}
                    >
                      {drawer}
                    </Drawer>
                  </Hidden>
                  <Hidden xsDown implementation="css">
                    <Drawer
                      classes={{
                        paper:
                          showTopBillingBar ? classes.drawerPaper2 : classes.drawerPaper,
                      }}
                      variant="permanent"
                      open
                      style={{ backgroundColor: !isDarkMode ? "#2C3440" : "" }}
                      PaperProps={{
                        style: drawerPaperOffsetStyle,
                      }}
                    >
                      {drawer}
                    </Drawer>
                  </Hidden>
                </nav>
              </>
            )}
            <main
              className={`${classes.content} dark:bg-[#1A1F2C]`}
              style={{
                marginTop: `${shellTopOffset}px`,
                position: "relative",
                backgroundColor: isDarkMode ? "#1A1F2C" : ""
              }}
            >
              {showLoader && <Loader layoutLoader={layoutLoader} />}
              <div
                className={classes.toolbarmirror}
                style={{ minHeight: "0px" }}
              />
              <div
                className="content-wrap MuiContainer-root dark:bg-[#1A1F2C]"
                style={{ backgroundColor: isDarkMode ? "#1A1F2C" : "" }}
              >
                {!isDeletionScheduled && (
                  <Suspense
                    fallback={
                      <div
                        className="min-h-[50vh] w-full animate-in fade-in duration-200"
                        aria-hidden
                      />
                    }
                  >
                    {props.children}
                  </Suspense>
                )}
              </div>
            </main>
            {!isInvoiceBuilderRoute && (
              <Suspense fallback={null}>
                <Footer />
              </Suspense>
            )}
          </div>
        </div>
      </CommonContext.Provider>
    </>
  );
};

export default DefaultLayouts;
